'use client';

import { Spinner } from '@lectura/ui';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';

/**
 * Kliens-oldali session-kapu.
 *
 * Biztonsági elv: a védett tartalom CSAK akkor renderelődik, ha van érvényes
 * (nem lejárt) access-token. Amint nincs érvényes session, AZONNAL eltüntetünk
 * mindent (teljes-képernyős spinner), és — ha a refresh-token sem él — a
 * /login-ra irányítunk. Bejelentkezetlen állapotban sosem villanhat fel
 * admin-tartalom.
 *
 * A korábbi verzió a gyermeket FELTÉTEL NÉLKÜL renderelte, és csak egy aszinkron
 * refresh BUKÁSA után redirektelt — így a session elvesztésének pillanatában a
 * régi (stale) felület látszott a login helyett. Ezt szünteti meg ez a kapu.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [hydrated, setHydrated] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Csak arra kell, hogy a kemény lejáratkor kikényszerítsünk egy újrarenderelést
  // (időmúlás magától nem renderel újra).
  const [, setTick] = useState(0);

  // Reaktív session-állapot: a store-ra FELIRATKOZUNK (nem getState), hogy a
  // token/expiry bármilyen változása azonnal újrarendereljen és a kaput vezérelje.
  const accessToken = useAuth((s) => s.accessToken);
  const expiresAt = useAuth((s) => s.expiresAt);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const isAuthRoute = pathname?.startsWith('/login') ?? false;
  // „Kemény" érvényesség: a token ténylegesen le nem járt (buffer nélkül). A
  // renderelés ezen múlik — amíg ez igaz, a tartalom látszik; amint hamis,
  // a tartalom eltűnik.
  const hardValid = !!accessToken && !!expiresAt && expiresAt > Date.now();

  // 1) Proaktív, csendes refresh a lejárat ELŐTT ~60 mp-cel, AMÍG még érvényes a
  //    token. Így aktív/jelenlévő usernél a session megújul, mielőtt eltűnne a
  //    tartalom — nincs felesleges spinner-villanás 15 percenként.
  useEffect(() => {
    if (!hydrated || isAuthRoute || !hardValid || !expiresAt) return;
    const lead = 60_000;
    const ms = Math.max(0, expiresAt - Date.now() - lead);
    const t = setTimeout(() => {
      void useAuth.getState().refresh();
    }, ms);
    return () => clearTimeout(t);
  }, [hydrated, isAuthRoute, hardValid, expiresAt]);

  // 2) Pontosan a kemény lejáratkor kényszerített újraértékelés: ekkor a hardValid
  //    hamisra fordul, és a 3) effekt veszi át (refresh-kísérlet / login).
  useEffect(() => {
    if (!hydrated || isAuthRoute || !expiresAt) return;
    const ms = expiresAt - Date.now();
    if (ms <= 0) return;
    const t = setTimeout(() => setTick((n) => n + 1), ms + 50);
    return () => clearTimeout(t);
  }, [hydrated, isAuthRoute, expiresAt]);

  // 3) Ha nincs érvényes token: EGY refresh-kísérlet (a 30 napos refresh-cookie
  //    még élhet); közben a tartalom el van takarva. Bukásnál /login-ra a
  //    `?next=` visszairányítóval.
  useEffect(() => {
    if (!hydrated || isAuthRoute) return;
    if (hardValid) return;
    let cancelled = false;
    setRefreshing(true);
    void useAuth
      .getState()
      .refresh()
      .then((ok) => {
        if (cancelled) return;
        setRefreshing(false);
        if (!ok) {
          const next = encodeURIComponent(pathname ?? '/');
          router.replace(`/login?next=${next}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hydrated, isAuthRoute, hardValid, pathname, router]);

  // A login/pre-auth oldalakat nem ez a komponens wrappeli; defenzív átengedés,
  // ha mégis kapu alá kerülnének.
  if (isAuthRoute) return <>{children}</>;

  // Bármi, ami NEM kemény-érvényes session → tartalom helyett teljes-képernyős
  // spinner (refresh fut, vagy épp a loginra redirektelünk). SOHA nem a gyermek.
  if (!hydrated || refreshing || !hardValid) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  return <>{children}</>;
}
